/**
 * Edit my profile: my photo, name, mobile and job title — and, for the
 * owner, the business's contact details customers and Lulu see.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import {
  ActionBar,
  Card,
  ErrorNote,
  Field,
  Spinner,
  SubHeader,
} from '../components/Primitives';
import { Icon } from '../components/Icon';
import { Photo } from '../components/Photo';

interface ProfileResponse {
  me: {
    fullName: string;
    email: string | null;
    phone: string | null;
    jobTitle: string | null;
    role: string;
    photoAt: string | null;
    canHavePhoto: boolean;
  };
  owner: boolean;
  business: {
    businessName: string;
    whatsapp: string | null;
    email: string | null;
    baseCity: string;
    leadTimeDays: number | null;
    capacityNote: string | null;
  } | null;
}

export function ProfileEditPage() {
  const T = useT();
  const { refresh } = useSession();
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState({ fullName: '', phone: '', jobTitle: '' });
  const [biz, setBiz] = useState({
    businessName: '',
    whatsapp: '',
    email: '',
    baseCity: '',
    leadTimeDays: '',
    capacityNote: '',
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const load = () =>
    api.get<ProfileResponse>('/v1/partner/me/profile').then(
      (r) => {
        setData(r);
        setMe({
          fullName: r.me.fullName,
          phone: r.me.phone ?? '',
          jobTitle: r.me.jobTitle ?? '',
        });
        if (r.business)
          setBiz({
            businessName: r.business.businessName,
            whatsapp: r.business.whatsapp ?? '',
            email: r.business.email ?? '',
            baseCity: r.business.baseCity,
            leadTimeDays: r.business.leadTimeDays?.toString() ?? '',
            capacityNote: r.business.capacityNote ?? '',
          });
      },
      (e: unknown) => setError(e instanceof ApiError ? e.message : 'Error'),
    );
  useEffect(() => {
    void load();
  }, []);

  if (error) return <ErrorNote message={error} onRetry={() => void load()} />;
  if (!data) return <Spinner />;

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
      await Promise.all([load(), refresh()]);
      setMsg({ text: ok, ok: true });
    } catch (e) {
      setMsg({
        text: e instanceof ApiError ? e.message : T('सेव नहीं हुआ।', 'Not saved.'),
        ok: false,
      });
    } finally {
      setBusy(null);
    }
  };

  const save = () =>
    run(
      'save',
      () =>
        api.post('/v1/partner/me/profile', {
          ...me,
          ...(data.owner
            ? {
                business: {
                  ...biz,
                  leadTimeDays: biz.leadTimeDays ? Number(biz.leadTimeDays) : null,
                },
              }
            : {}),
        }),
      T('सेव हो गया ✓', 'Saved ✓'),
    );

  return (
    <div className="space-y-4">
      <SubHeader icon="profile" title={T('मेरी प्रोफ़ाइल', 'My profile')} />

      <Card className="flex items-center gap-4">
        <div className="relative">
          <Photo name={me.fullName} photoAt={data.me.photoAt} className="h-20 w-20 text-2xl" />
          {data.me.canHavePhoto && (
            <button
              type="button"
              aria-label={T('फ़ोटो बदलें', 'Change photo')}
              className="absolute -bottom-1 -right-1 flex !min-h-0 h-9 w-9 items-center justify-center rounded-full bg-panel shadow-card ring-1 ring-line"
              onClick={() => file.current?.click()}
            >
              <Icon name="camera" className="h-4 w-4" />
            </button>
          )}
          <input
            ref={file}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(e) => {
              const pic = e.target.files?.[0];
              e.target.value = '';
              if (pic)
                void run(
                  'photo',
                  () => api.put('/v1/partner/me/photo', pic),
                  T('फ़ोटो सेव ✓', 'Photo saved ✓'),
                );
            }}
          />
        </div>
        <div className="min-w-0">
          <p className="font-display text-lg font-bold">{data.me.fullName}</p>
          <p className="text-sm text-muted">
            {data.me.role === 'owner' ? T('मालिक', 'Owner') : T('टीम', 'Team')}
            {data.me.email ? ` · ${data.me.email}` : ''}
          </p>
          {data.me.canHavePhoto ? (
            data.me.photoAt && (
              <button
                type="button"
                className="btn-quiet mt-1 !min-h-0 text-sm"
                disabled={busy !== null}
                onClick={() =>
                  void run(
                    'photo',
                    () => api.del('/v1/partner/me/photo'),
                    T('हटा दी', 'Removed'),
                  )
                }
              >
                {T('फ़ोटो हटाएँ', 'Remove photo')}
              </button>
            )
          ) : (
            <p className="mt-1 text-xs text-muted">
              {T(
                'फ़ोटो के लिए ईमेल से साइन इन करें।',
                'Sign in with your email to add a photo.',
              )}
            </p>
          )}
          {busy === 'photo' && (
            <p className="text-xs text-muted">{T('भेज रहे हैं…', 'Uploading…')}</p>
          )}
        </div>
      </Card>

      <Card className="space-y-4">
        <Field label={T('पूरा नाम', 'Full name')}>
          <input
            value={me.fullName}
            onChange={(e) => setMe({ ...me, fullName: e.target.value })}
          />
        </Field>
        <Field label={T('मोबाइल', 'Mobile')}>
          <input
            type="tel"
            inputMode="tel"
            className="tnum"
            value={me.phone}
            onChange={(e) => setMe({ ...me, phone: e.target.value })}
          />
        </Field>
        <Field label={T('काम / पद', 'Job title')}>
          <input
            placeholder={T('जैसे: इंस्टॉलेशन इंचार्ज', 'e.g. Installation in-charge')}
            value={me.jobTitle}
            onChange={(e) => setMe({ ...me, jobTitle: e.target.value })}
          />
        </Field>
      </Card>

      {data.owner && (
        <Card className="space-y-4">
          <h2 className="font-display text-lg font-bold">
            {T('बिज़नेस की जानकारी', 'Business details')}
          </h2>
          <Field label={T('बिज़नेस का नाम', 'Business name')}>
            <input
              value={biz.businessName}
              onChange={(e) => setBiz({ ...biz, businessName: e.target.value })}
            />
          </Field>
          <Field label="WhatsApp">
            <input
              type="tel"
              inputMode="tel"
              className="tnum"
              value={biz.whatsapp}
              onChange={(e) => setBiz({ ...biz, whatsapp: e.target.value })}
            />
          </Field>
          <Field label={T('बिज़नेस ईमेल', 'Business email')}>
            <input
              type="email"
              value={biz.email}
              onChange={(e) => setBiz({ ...biz, email: e.target.value })}
            />
          </Field>
          <Field label={T('शहर', 'City')}>
            <input
              value={biz.baseCity}
              onChange={(e) => setBiz({ ...biz, baseCity: e.target.value })}
            />
          </Field>
          <Field
            label={T('आम तौर पर काम में कितने दिन', 'Usual days to finish a job')}
            hint={T('ग्राहक को अंदाज़ा मिलता है।', 'Gives customers an idea.')}
          >
            <input
              inputMode="numeric"
              className="tnum"
              value={biz.leadTimeDays}
              onChange={(e) =>
                setBiz({ ...biz, leadTimeDays: e.target.value.replace(/\D/g, '').slice(0, 2) })
              }
            />
          </Field>
          <Field label={T('क्षमता / ख़ास बात', 'Capacity / what you’re good at')}>
            <textarea
              rows={3}
              value={biz.capacityNote}
              placeholder={T(
                'जैसे: 40 होर्डिंग, रात में भी फ़िटिंग',
                'e.g. 40 hoardings, night installs',
              )}
              onChange={(e) => setBiz({ ...biz, capacityNote: e.target.value })}
            />
          </Field>
        </Card>
      )}

      {msg && (
        <p className={`text-sm ${msg.ok ? 'text-good' : 'text-bad'}`} role="status">
          {msg.text}
        </p>
      )}
      <ActionBar>
        <button
          type="button"
          className="btn-primary"
          disabled={busy !== null || me.fullName.trim().length < 2}
          onClick={() => void save()}
        >
          {busy === 'save' ? T('सेव हो रहा है…', 'Saving…') : T('सेव करें', 'Save')}
        </button>
      </ActionBar>
    </div>
  );
}
